import { BoxProps, Flex } from "@chakra-ui/react";
import { BouncyImage } from "./BouncyImage";
import { DoubleBouncyImage } from "./DoubleBouncyImage";
import { useHorizontalReader } from "../../hooks/useHorizontalReader";
import { usePreloadPages } from "../../hooks/usePreloadPages";
import { useTrackPageView } from "../../hooks/useTrackPageView";
import { EndInterstitial } from "../interstitals/EndInterstitial";
import { useP2PSettings } from "../../hooks/useP2PSettings";
import { useIsOverlayOpen } from "../../hooks/hookstate/useIsOverlayOpen";
import { useP2PUrl } from "../../hooks/useP2PUrl";
import { P2PReader } from "./P2PReader";
import { useCallback, useEffect } from "react";
import isTouchDevice from "../../../../utils/isTouchDevice";
import { useGestureControls } from "../../hooks/useGestureControls";
import { useReaderPaywall } from "../../hooks/useReaderPaywall";
import { useHookstate } from "@hookstate/core";
import { rootComponentsStore } from "../../../../hookstate/rootComponentsStore";
import { useSlideInMenu } from "../../../../components/slideInMenu/hooks/useSlideInMenu";
import { useParams } from "react-router";
import { useReleaseData } from "../../hooks/hookstate/useReleaseData";
import { useNavigateToReaderWithContext } from "../../hooks/useNavigateToReaderWithContext";
import { NavigationTrigger } from "../../types";
import { readerNavigationContext } from "../../../../hookstate/readerNavigationContextStore";
import { Release } from "../../../../types";

interface HorizontalReaderProps {
  readingDirection: string;
}
export function HorizontalReader({ readingDirection }: HorizontalReaderProps) {
  useTrackPageView();
  useP2PUrl();

  const { openComicInfoMenu } = useSlideInMenu();
  const { setIsOverlayOpen } = useIsOverlayOpen();

  const { pageOrder } = useParams();
  const { releaseData } = useReleaseData();
  const {
    showPaywall,
    showEndInterstitial,
    releasePageData,
    doubleReleasePageData,
    releasePagesData,
    directionMoved,
    fullHeightReader,
    readerHorizontalScroll,
  } = useHorizontalReader(readingDirection);

  usePreloadPages(releasePagesData);

  // Calculate the page to navigate to when paywall closes
  const calculateFreePage = useCallback(() => {
    let newPage = releaseData?.free_page_count ?? 0;
    if (newPage === parseInt(pageOrder ?? "")) {
      newPage--;
    }
    return newPage;
  }, [releaseData, pageOrder]);

  // Get the existing context and preserve it with updated trigger
  const currentContext = useHookstate(readerNavigationContext).get();
  const { goToCTAUrl: navigateToFreePage } = useNavigateToReaderWithContext(
    {
      ...currentContext,
      releaseToNavigate: releaseData as Release | undefined,
      trigger: NavigationTrigger.paywallClose,
      pageToNavigate: calculateFreePage(),
    },
    [releaseData, currentContext, calculateFreePage()]
  );

  const onClose = useCallback(() => {
    // In some double page cases, we show a free and paid page
    // at the same time. This will make sure to resolve to a page
    // that shows no paywall.
    const newPage = calculateFreePage();
    if (newPage) {
      navigateToFreePage();
    } else {
      setIsOverlayOpen(true);
      const comicSlug = releaseData?.comic?.slug;
      if (comicSlug) {
        openComicInfoMenu(comicSlug, {
          ...(currentContext ?? {}),
          area: "consumption",
        });
      }
    }
  }, [
    calculateFreePage,
    currentContext,
    navigateToFreePage,
    openComicInfoMenu,
    releaseData,
    setIsOverlayOpen,
  ]);

  const openType = useHookstate(rootComponentsStore.funnelModal.type).get();
  useReaderPaywall({ showPaywall, openType, onClose });

  const { x, opacity } = useGestureControls(directionMoved, releasePageData);
  const { p2pEnabledAndAugmented } = useP2PSettings();
  const isTouch = isTouchDevice();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [releasePageData]);

  const swipeProps = { opacity, x };
  const horizontalReaderProps: BoxProps = !p2pEnabledAndAugmented
    ? {
        flexDirection: "column",
        style: {
          touchAction: isTouch ? "auto" : "none",
        },
      }
    : {};

  return (
    <Flex
      id="horizontalReader"
      display="flex"
      alignItems="center"
      overflow="hidden"
      margin="auto"
      height="fit-content"
      {...horizontalReaderProps}
    >
      {p2pEnabledAndAugmented ? (
        <P2PReader releasePageData={releasePageData} swipeProps={swipeProps} />
      ) : !doubleReleasePageData ? (
        <BouncyImage
          fullHeightReader={fullHeightReader}
          releasePageData={releasePageData}
          swipeProps={swipeProps}
        />
      ) : (
        <DoubleBouncyImage
          readingDirection={readingDirection}
          fullHeightReader={readerHorizontalScroll}
          releasePageData={releasePageData}
          doubleReleasePageData={doubleReleasePageData}
          swipeProps={swipeProps}
        />
      )}
      {showEndInterstitial && <EndInterstitial />}
    </Flex>
  );
}
